import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Drawer, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { MemberContext } from "../../hooks/MemberContext";
import { useAuth } from "../../hooks/useAuth";
import { useSponsorType } from "../../hooks/useSponsorType";
import { getSponsorUnicityId, getCustomerCategory } from "../../common/helper-functions";
import {
  PARTNERS_PAGE_PATH,
  isDirectSponseeOfUser,
  isSponseeOfOwnMember,
} from "../../common/memberProfileAccess";
import { T } from "../../hooks/useTranslate";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MemberProfileDrawerContent } from "./MemberProfileDrawerContent";
import { OrderSummaryDrawerContent } from "../../components/drawer/OrderSummaryDrawerContent";
import { OrderDetailDrawerContent } from "../../components/drawer/OrderDetailDrawerContent";
import { ReferralSummaryDrawerContent } from "../../components/drawer/ReferralSummaryDrawerContent";

const drawerContentComponents = {
  profile: MemberProfileDrawerContent,
  orders: OrderSummaryDrawerContent,
  orderDetails: OrderDetailDrawerContent,
  referrals: ReferralSummaryDrawerContent,
};

const MembersDrawer = () => {
  const [drawerContent, setDrawerContent] = useState(null);
  const [orderHref, setOrderHref] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDrawerOpen, setIsDrawerOpen, setMemberId, resetContext, customerHrefData, errorMessage } = useContext(MemberContext);
  const { userInfo } = useAuth();

  const isPartnersPage = PARTNERS_PAGE_PATH.test(location.pathname);
  // sponsor data only exists on customerHrefData ({href}?expand=
  // profilePicture), not memberData (customers?unicity=X&expand=customer) --
  // that endpoint never returns a sponsor field at all. See getSponsorUnicityId.
  const memberSponsorId = getSponsorUnicityId(customerHrefData);
  const memberOwnType = getCustomerCategory(customerHrefData?.type);
  // narrower than apiChainInProgress, which also waits on 5 unrelated
  // auxiliary fetches (orders, referrals, notes, level limit, order count).
  // customerHrefData is set as soon as its own fetch resolves -- in the
  // prefetched-navigation path (RankQualBox) that happens well before
  // apiChainInProgress flips false, so this narrower gate still matters there.
  const sponsorKnown = Boolean(customerHrefData) && customerHrefData !== false && memberSponsorId !== undefined;
  const isDirectSponsee =
    sponsorKnown && isDirectSponseeOfUser(memberSponsorId, userInfo?.unicityId);

  // ---------------------------------------------------------------------------
  // Who can open a MEMBER's profile (GO-3345 round 2, PM 2026-08-05)
  //
  // Exactly the top two levels of the user's own organisation, and only
  // through another member:
  //   1. the user's own frontline members  (the member's sponsor IS the user)
  //   2. members sponsored by one of those frontline members
  // Anything deeper is blocked, REGARDLESS of the account type of the member's
  // sponsor. Equivalently: no Distributor may sit between the user and the
  // member, AND the chain may be at most two hops long.
  //
  // This REPLACES GO-2724's "member-of-member is unlimited depth" rule, which
  // is what let a user open members arbitrarily deep in the organisation as
  // long as each link happened to be a member -- the over-permissiveness found
  // while testing the round-1 fix. Depth, not the sponsor's type, is now the
  // binding constraint; the sponsor's type only decides whether a distributor
  // is in the way.
  //
  // PARTNERS are unaffected and remain viewable at any depth (round 1), which
  // is why the sponsor lookup is skipped for them entirely now: it cannot
  // change the answer, and it costs up to two requests.
  //
  // Depth beyond this is a separate, backend-owned concern: the
  // isDistributorLevelLimit flag (MemberContext's
  // {OFFICE_API}/{unicityId}/distributorLevelLimit?partnerId= fetch) gates
  // descending FURTHER -- the "see frontline" button in
  // DistributorProfileDrawerContent.jsx, RankQualBox leg clicks -- not whether
  // a profile opens.
  // ---------------------------------------------------------------------------
  const targetIsMember = memberOwnType === "member";
  const needsSponsorLookup = sponsorKnown && targetIsMember && !isDirectSponsee;
  const { sponsorType, sponsorSponsorId, sponsorTypeKnown } = useSponsorType(
    needsSponsorLookup ? memberSponsorId : undefined
  );

  // Level 2: the sponsor must itself be a member (else a distributor is in the
  // way) AND be sponsored by the user. The rule (and the undefined-vs-undefined
  // hazard notes) now lives in common/memberProfileAccess.js, shared with the
  // GO-3366 click-time gate in RankQualBox so the two can't drift apart.
  const memberViewable =
    isDirectSponsee ||
    isSponseeOfOwnMember({
      sponsorType,
      sponsorSponsorId,
      userUnicityId: userInfo?.unicityId,
    });

  const accessCheckResolved =
    sponsorKnown && (!needsSponsorLookup || sponsorTypeKnown);
  const isAccessDenied =
    isPartnersPage &&
    accessCheckResolved &&
    targetIsMember &&
    !memberViewable;
  // isAccessDenied is false both while genuinely allowed AND while still
  // loading (accessCheckResolved gates the whole expression to false, not
  // "unknown") -- a plain isAccessDenied ? deny : content ternary can't tell
  // those apart, so it renders content optimistically during the loading
  // window, a real profile briefly visible before the check flips to
  // denied. accessKnown gates content rendering on the check having
  // actually resolved either way.
  // If the profile fetch itself fails/401s, customerHrefData settles to
  // false and accessCheckResolved never resolves -- without this, the
  // drawer would spin forever with no error state, distinct from (and worse
  // than) the intentional access-denied screen. errorMessage is already set
  // by MemberContext on that failure; folding it in here lets
  // DrawerContentComponent render and hit its own existing `if
  // (errorMessage) return <MemberErrorScreen />` check -- reusing that
  // rather than building a second error path. Safe: no member data is
  // exposed by this, the content component's own check intercepts first.
  const accessKnown = isPartnersPage ? accessCheckResolved || Boolean(errorMessage) : true;

  const theme = useTheme();
  const viewportIsSmall = !useMediaQuery(theme.breakpoints.up("sm"));

  const handleClose = () => {
    setIsDrawerOpen(false);
    resetContext();
    navigate(`${location.pathname}`, { replace: true });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const drawer = searchParams.get("drawer");
    if (drawer === "member") {
      setIsDrawerOpen(true);
      setDrawerContent(searchParams.get("drawerContent"));
      setMemberId(searchParams.get("id"));
      setOrderHref(searchParams.get("orderHref"));
    } else {
      setIsDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const DrawerContentComponent = drawerContentComponents[drawerContent];

  return (
    <div>
      <Drawer anchor="right" open={isDrawerOpen} onClose={handleClose}>
        <Box
          sx={{
            width: "100vw",
            maxWidth: !viewportIsSmall ? 400 : null,
            padding: 0,
          }}
          role="presentation"
        >
          {isAccessDenied ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "25px" }}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 40, gap: "15px", flexDirection: "column" }}>
                <ErrorIcon />
                <Typography><T>access_denied</T></Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  onClick={handleClose}
                  sx={{ height: "31px", color: "#FFFFFF", backgroundColor: "#009ECF", fontWeight: "600", fontSize: "12px", pl: "12px", pr: "12px", borderRadius: "4px", "&:hover": { backgroundColor: "rgba(0,158,198,0.48)" } }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          ) : !accessKnown ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 500 }}>
              <CircularProgress data-testid="access-check-progress-bar" />
            </Box>
          ) : (
            DrawerContentComponent && <DrawerContentComponent orderHref={orderHref} />
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export { MembersDrawer };
